import { StateManager, ApiStateSingle } from 'services';
import { IndexedObject } from 'services/indexedObject';

import { API_URL } from 'settings';

import * as types from './types';
import {
  AdminETFDetails,
  AdminAIPlayerOption,
  AdminAISessionContextType,
  AdminAISessionOption,
  AssetEodPreview,
  AssetIntradayPreview,
  ClientDealingScenarioDataType,
  ClientDealingScenarioType,
  ExecutedMoosPartialOrderType,
  ExecutionErrorType,
  ForwardContract,
  IndexFund,
  IndexFundPreview,
  LastOpenPricesType,
  LLMGroupResponseType,
  LLMResponseType,
  BiasReportType,
  RegimeOverlayType,
  MacroBackgroundType,
  MarketHistoryCSVType,
  MarketHoursType,
  OrderExecutionError,
  PartialOrderType,
  Player,
  PlayerWithDifferences,
  PromptType,
  TargetAllocationsByInvestmentProfile,
  TriedAndFailedOrderType,
  YieldDownloadType,
} from './types';
import { receiveCreatedAuctionType } from './auctions/types';
import { ClientMoney, ClientMoneyInitialState } from './client_money/types';
import {
  OptionsContractsData,
  OptionsContractsDataInitialState,
  OptionsContractType,
  OptionsInstrumentsInitialState,
} from './options/types';
import {
  DevPlanLaunchSelection,
  DevPlanState,
  DevPlanSummary,
} from './devplan/types';
import { DevPlanAdminState } from './devplanAdmin/types';
import { SyntheticBondPlaygroundState } from './syntheticBondPlayground/types';
import {
  initialSyntheticBondGenerationDraft,
  SyntheticBondGenerationState,
} from './syntheticBonds/types';
import { AssessmentInitialState, AssessmentState } from './assessment/types';
import type { InspectAssessmentState } from './inspect_assessment/types';
import type { InspectUserState } from './inspect_user/types';

export type API<T> = {
  items: IndexedObject<T>;
  waiting: boolean;
  error?: any;
  // True from a batch-generation request (optimistic on dispatch) until the
  // server's `*_generation_finished` websocket bracket clears it. Drives the
  // admin Generate-button lock for bias / individual reports. Ephemeral (lost on
  // refresh — the admin is instructed to wait).
  generating?: boolean;
};

export type API2<T> = {
  items: IndexedObject<T>;
  fetching: boolean;
  fetched: any;
};

export type AppState = {
  badges: API<types.Badge>;
  klasses: API<types.Klass>;
  create_klass_waiting: boolean;
  persons: API<types.Person>;
  sessions: API<types.Session>;
  create_session_request: {
    waiting: boolean;
    succeeded: boolean;
    failed: boolean;
  };
  replay_session_defaults: {
    item?: types.ReplaySessionDefaults;
    source_session_id?: number;
    waiting: boolean;
    fetched: boolean;
  };
  // Admin cross-trainer "Replay into..." target-class picker options.
  replay_target_klasses: {
    items: types.ReplayTargetKlass[];
    waiting: boolean;
    fetched: boolean;
  };
  paginated_sessions: {
    items: types.Session[];
    waiting: boolean;
    // Completed list loads only; unrelated row edits must not refetch counts.
    revision?: number;
    error?: unknown;
    page_number?: number;
    page_size?: number;
    count?: number;
  };
  auctions: receiveCreatedAuctionType[];
  active_session: number;
  retrieving_session?: number;
  selected_person: number;
  players: API<types.Player> & {
    // Independent report requests and live events can beat roster hydration.
    pending_updates?: IndexedObject<Partial<types.Player>>;
  };
  player: ApiStateSingle<types.Player>;
  underlying_assets: API<types.UnderlyingAsset> & { fetched: boolean };
  lseg_eod_quota: types.LsegEodQuotaState;
  intraday_fetch_status: types.IntradayFetchState;
  eod_fetch_status: types.IntradayFetchState;
  etf_details: API2<AdminETFDetails>;
  options_contracts_data: OptionsContractsData; // indexed by options asset id
  underlying_deposits: API<types.DepositUnderlying>;
  fx_cross: API<types.FXCross>;
  equities: API<types.Equity> & { fetched: boolean };
  dividends: API<types.Dividend> & { fetched: boolean };
  presets: API<types.Preset> & {
    trainerAvailability: IndexedObject<types.PresetTrainerAvailability>;
    availabilityWaiting?: boolean;
    unhidingAssetId?: number;
  };
  blueprints: API<types.Blueprint>;
  initial_presets: API<types.initialPreset>;
  currencies: API<types.Currency> & { reportingCurrency?: number };
  failedExecutions: OrderExecutionError[];
  orders: API<types.Order> & { last?: types.Order; fetching?: boolean };
  client_money: ClientMoney & { fetched: boolean; fetching?: boolean };
  // forward_contracts: API<types.ForwardContract> & {
  //   fetched: boolean;
  //   fetching?: boolean;
  // };
  index_funds: API<types.IndexFund>;
  news: API<types.News> & {
    last?: IndexedObject<types.News>;
    fetching?: boolean;
    historical_delivery?: types.HistoricalNewsDeliveryState;
  };
  initialPortfolioStatus: {
    [sessionId: number]: types.InitialBuyStatus;
  };
  individualReports: {
    [playerID: number]: types.IndividualReport;
  };
  devplan: {
    plans: DevPlanSummary[]; // GET /devplan/plans — pickable trees
    plansFetched: boolean;
    item?: DevPlanState; // the currently-open tree (/plans/<code>/me)
    itemPlanCode?: string; // which tree `item` holds
    fetched: boolean;
    error?: string;
    // The solo room a node launch just opened; the view navigates on it, then clears it.
    launch?: {
      sessionId: number;
      resumed: boolean;
      selection?: DevPlanLaunchSelection;
    };
  };
  // Admin multi-tree editor (physically separate from the trainee `devplan` slice).
  devplanAdmin: DevPlanAdminState;
  synthetic_bond_playground: SyntheticBondPlaygroundState;
  synthetic_bonds: SyntheticBondGenerationState;
  correlationMatrix: ApiStateSingle<types.CorrelationMatrix> & {
    fetched: boolean;
  };
  correlationValues: API<types.CorrelationValue> & {
    fetched: boolean;
  };
  correlationValuesWaitingList: number[][];
  trainers: API<types.Trainer>;
  insightsReports: API<types.InsightsReport> & { creating: boolean };
  roadmapItems: API<types.RoadmapItem>;
  market_history: API<types.MarketHistory>;
  stock_splits: API<types.StockSplit> & { fetched: boolean };
  error?: string;
  admin: {
    betas: { [key: number]: types.EquityBeta };
    volatilities: { [key: number]: types.EquityVolatility };
    message?: string;
  };
  errors_tab: {
    execution_errors: {
      items: IndexedObject<ExecutionErrorType>;
      fetched: boolean;
      fetching?: boolean;
    };
    tried_and_failed_orders: {
      items: IndexedObject<TriedAndFailedOrderType>;
      fetched: boolean;
      fetching?: boolean;
    };
    players_with_differences: {
      items: IndexedObject<PlayerWithDifferences>;
      fetched: boolean;
      fetching?: boolean;
    };
    pending_moos: {
      items: IndexedObject<PartialOrderType>;
      fetched: boolean;
      fetching?: boolean;
    };
    moos_executed_today: {
      items: IndexedObject<ExecutedMoosPartialOrderType>;
      fetched: boolean;
      fetching?: boolean;
    };
    pending_sl_tps: {
      items: IndexedObject<PartialOrderType>;
      fetched: boolean;
      fetching?: boolean;
    };
  };
  historical_period_length: number; // no longer used, switched to session.historical_sleep_time
  index_fund_preview: IndexFundPreview;
  asset_eod_preview: AssetEodPreview;
  asset_intraday_preview: AssetIntradayPreview;
  client_dealing_scenarios: API2<ClientDealingScenarioType>;
  downloaded_scenario: ClientDealingScenarioDataType[];
  market_hours: {
    items: IndexedObject<MarketHoursType>;
    fetched: boolean;
    fetching?: boolean;
  };
  last_open_prices: {
    items: IndexedObject<LastOpenPricesType>;
    header: string[];
    fetched: boolean;
    fetching?: boolean;
  };
  last_close_prices: {
    items: IndexedObject<LastOpenPricesType>;
    header: string[];
    fetched: boolean;
    fetching?: boolean;
  };
  market_history_download_csv: {
    items: IndexedObject<MarketHistoryCSVType>;
    fetched: boolean;
    fetching?: boolean;
  };
  yield_download: {
    items: YieldDownloadType[];
    fetched: boolean;
    fetching?: boolean;
  };
  investment_profiles: {
    items: IndexedObject<TargetAllocationsByInvestmentProfile>;
    fetched: boolean;
    fetching?: boolean;
  };
  macro_backgrounds: API<MacroBackgroundType>;
  prompts: API<PromptType>;
  group_prompts: API<PromptType>;
  llm_responses: API<LLMResponseType>;
  llm_group_responses: API<LLMGroupResponseType>;
  bias_reports: API<BiasReportType>;
  market_regimes: {
    waiting: boolean; // compute (generate) in flight
    fetching: boolean; // overlay fetch in flight
    overlay: RegimeOverlayType | null;
  };
  admin_ai_sessions: API<AdminAISessionOption>;
  admin_ai_players: API<AdminAIPlayerOption>;
  admin_ai_session_context: {
    item: AdminAISessionContextType | null;
    waiting: boolean;
    requested_session_id: number | null;
    error?: any;
  };
  assessment: AssessmentState;
  inspect_assessment: InspectAssessmentState;
  inspect_user: InspectUserState;
  eod_price_segments: {
    items: { x: string; y: number }[][];
    fetched: boolean;
    fetching?: boolean;
  };
};

export const appManager = new StateManager({
  moduleName: 'app',
  apiUrl: API_URL,
  initialState: {
    badges: { items: {} },
    currencies: { items: {} },
    klasses: { items: {} },
    create_klass_waiting: false,
    persons: { items: {} },
    orders: { items: {} },
    forward_contracts: { items: {}, fetched: false, fetching: false },
    client_money: ClientMoneyInitialState,
    players: { items: {} },
    player: { item: { options_instruments: OptionsInstrumentsInitialState } },
    sessions: { items: {} },
    create_session_request: {
      waiting: false,
      succeeded: false,
      failed: false,
    },
    replay_session_defaults: {
      item: undefined,
      source_session_id: undefined,
      waiting: false,
      fetched: false,
    },
    replay_target_klasses: {
      items: [],
      waiting: false,
      fetched: false,
    },
    auctions: { items: {} },
    paginated_sessions: { items: [] },
    options_contracts_data: OptionsContractsDataInitialState,
    underlying_assets: { items: {} },
    lseg_eod_quota: { loading: false, refreshing: false },
    intraday_fetch_status: { loading: false },
    eod_fetch_status: { loading: false },
    etf_details: { items: {}, fetched: false, fetching: false },
    underlying_deposits: { items: {} },
    equities: { items: {} },
    fx_cross: { items: {} },
    dividends: { items: {} },
    index_funds: { items: {} },
    news: { items: {} },
    presets: { items: {}, trainerAvailability: {} },
    blueprints: { items: {} },
    initial_presets: { items: {} },
    initialPortfolioStatus: {},
    individualReports: {},
    devplan: {
      plans: [],
      plansFetched: false,
      item: undefined,
      fetched: false,
    },
    devplanAdmin: {
      plans: [],
      plansFetched: false,
      content: undefined,
      contentFetched: false,
      assets: [],
      assetsFetched: false,
      saving: 0,
      recommendations: [],
      recTargets: [],
      recTargetsFetched: false,
    },
    synthetic_bond_playground: {
      bonds: [],
      fetched: false,
      fetching: false,
    },
    synthetic_bonds: {
      options: undefined,
      optionsFetching: false,
      optionsError: undefined,
      series: undefined,
      seriesFetching: false,
      seriesError: undefined,
      seriesSaving: false,
      seriesSaveError: undefined,
      catalogue: undefined,
      catalogueFetching: false,
      catalogueError: undefined,
      sourceAssetSearchByKey: {},
      referenceCurvePresetsByCurrencyKey: {},
      selectedReferenceCurvePresetId: undefined,
      referenceCurvePresetSaving: false,
      referenceCurvePresetSaveError: undefined,
      referenceCurvesByFamilyKey: {},
      candidatePreviewByKey: {},
      draftSpec: initialSyntheticBondGenerationDraft(),
      draftRevision: 0,
      preview: undefined,
      previewing: false,
      previewError: undefined,
      previewRequestId: undefined,
      previewRequestRevision: undefined,
      previewV2JobsById: {},
      candidatePreviewsV2ByJobId: {},
      candidatePreviewChunksV2ByJobId: {},
      activePreviewV2JobId: undefined,
      previewV2: undefined,
      previewingV2: false,
      previewV2Error: undefined,
      previewV2RequestId: undefined,
      previewV2RequestRevision: undefined,
      previewV2StatusFetching: false,
      createSubmitting: false,
      createError: undefined,
      activeBatchId: undefined,
      activeBatch: undefined,
      statusFetching: false,
      statusError: undefined,
      statusRequestId: undefined,
      retrying: false,
      retryError: undefined,
      retryRequestId: undefined,
    },
    insightsReports: { items: {}, creating: false },
    roadmapItems: { items: {} },
    trainers: { items: {} },
    correlationMatrix: { item: {} },
    correlationValues: { items: {} },
    market_history: { items: {} },
    stock_splits: { items: {} },
    correlationValuesWaitingList: [],
    client_dealing_scenarios: {
      items: {},
      fetched: false,
      fetching: false,
    },
    admin: {
      beta: {},
      volatility: {},
      message: undefined,
    },
    market_hours: { items: {} },
    errors_tab: {
      execution_errors: {
        items: {},
        fetched: false,
        fetching: false,
      },
      tried_and_failed_orders: {
        items: {},
        fetched: false,
        fetching: false,
      },
      players_with_differences: {
        items: {},
        fetched: false,
        fetching: false,
      },
      pending_moos: {
        items: {},
        fetched: false,
        fetching: false,
      },
      moos_executed_today: {
        items: {},
        fetched: false,
        fetching: false,
      },
      pending_sl_tps: {
        items: {},
        fetched: false,
        fetching: false,
      },
    },
    last_open_prices: {
      items: {},
      header: [],
      fetched: false,
      fetching: false,
    },
    last_close_prices: {
      items: {},
      header: [],
      fetched: false,
      fetching: false,
    },
    market_history_download_csv: {
      items: {},
      fetched: false,
      fetching: false,
    },
    yield_download: {
      items: [],
      fetched: false,
      fetching: false,
    },
    historical_period_length: 180,
    investment_profiles: {
      items: {},
      fetched: false,
      fetching: false,
    },
    macro_backgrounds: {
      items: {},
      waiting: false,
    },
    prompts: {
      items: {},
      waiting: false,
    },
    group_prompts: {
      items: {},
      waiting: false,
    },
    llm_responses: {
      items: {},
      waiting: false,
    },
    llm_group_responses: {
      items: {},
      waiting: false,
    },
    bias_reports: {
      items: {},
      waiting: false,
    },
    market_regimes: {
      waiting: false,
      fetching: false,
      overlay: null,
    },
    admin_ai_sessions: {
      items: {},
      waiting: false,
    },
    admin_ai_players: {
      items: {},
      waiting: false,
    },
    admin_ai_session_context: {
      item: null,
      waiting: false,
      requested_session_id: null,
    },
    eod_price_segments: {
      items: [],
      fetched: false,
      fetching: false,
    },
    assessment: AssessmentInitialState,
    inspect_assessment: {
      assessments: [],
      assessmentsFetching: false,
      assessmentsFetched: false,
      klasses: [],
      klassesFetching: false,
      klassesFetched: false,
      users: [],
      usersFetching: false,
      userAssignmentsByContextKey: {},
      instruments: [],
      instrumentsFetching: false,
      periods: [],
      periodsFetching: false,
      periodNewsByIndex: {},
      periodNewsFetching: false,
      periodNewsBatchFetchingByIndex: {},
      periodPricesByKey: {},
      periodPricesFetchingByKey: {},
      periodPricesBatchFetchingByKey: {},
      periodPricesErrorByKey: {},
      periodBatchFetching: false,
      sliderDirectionByRequestKey: {},
      sliderDirectionFetchingByRequestKey: {},
      sliderDirectionFetchedByRequestKey: {},
      sliderDirectionErrorByRequestKey: {},
      optimalPortfolioByRequestKey: {},
      optimalPortfolioFetchingByRequestKey: {},
      optimalPortfolioFetchedByRequestKey: {},
      optimalPortfolioErrorByRequestKey: {},
      candidatePositionsByRequestKey: {},
      candidatePositionsFetchingByRequestKey: {},
      candidatePositionsFetchedByRequestKey: {},
      candidatePositionsErrorByRequestKey: {},
    },
    inspect_user: {
      klasses: [],
      klassesFetching: false,
      klassesFetched: false,
      users: [],
      usersFetching: false,
      assessmentSessions: [],
      assessmentSessionsFetching: false,
    },
  },
  tokenSelector: state => state.auth.item.token,
  selectors: [
    { varName: 'session_id', selector: state => state.app.active_session },
    {
      varName: 'person_id',
      selector: state => state.auth.item.selected_person,
    },
  ],
});
